import { VIEW_SEQUENCE } from '../constants';
import type { CapturedImage, CaptureSequence } from '../types';
import type { CapturedImageClientState } from '@/lib/types';

/** The manual floor correction of a view: the angle and pivot the evaluation applies, and the floor and plumb handles that produced them. */
type FloorCorrectionFields = Pick<
  CapturedImageClientState,
  'floorCorrectionDeg' | 'floorCorrectionPivot' | 'floorCorrectionSource' | 'manualFloorPoints' | 'plLandmarks'
>;

type SavedView = Pick<CapturedImageClientState, 'imageData' | 'landmarks' | 'cmPerPx' | 'custom' | 'pelvisPinsOptOut' | 'captureSource'> &
  FloorCorrectionFields;

/**
 * A view's manual floor correction, as stored on a saved Posture analysis. Only a manual correction
 * is kept: it is the only one `evaluateFullPostureAnalysis` applies, so a reopened analysis gives the
 * same results as when it was saved. Without one, the view is evaluated against a straight vertical.
 */
export function pickManualFloorCorrection(
  view: Partial<FloorCorrectionFields> | null | undefined,
): FloorCorrectionFields {
  if (view?.floorCorrectionSource !== 'manual' || typeof view.floorCorrectionDeg !== 'number') return {};
  return JSON.parse(
    JSON.stringify({
      floorCorrectionSource: 'manual',
      floorCorrectionDeg: view.floorCorrectionDeg,
      floorCorrectionPivot: view.floorCorrectionPivot ?? null,
      ...(view.manualFloorPoints ? { manualFloorPoints: view.manualFloorPoints } : {}),
      ...(view.plLandmarks ? { plLandmarks: view.plLandmarks } : {}),
    }),
  );
}

/**
 * The `captured` map persisted on a Posture analysis: complete views only, with plain landmark
 * objects, the capture source when known (Remote Assessment marks `screen-share`), and the manual
 * floor correction when the practitioner set one.
 */
export function buildCapturedViewsForSave(
  capturedImages: Record<CaptureSequence, CapturedImage | null>,
): Partial<Record<CaptureSequence, SavedView>> {
  const out: Partial<Record<CaptureSequence, SavedView>> = {};
  for (const view of VIEW_SEQUENCE) {
    const image = capturedImages[view];
    if (image?.status === 'complete' && image.imageData && image.landmarks) {
      out[view] = {
        imageData: image.imageData,
        landmarks: JSON.parse(JSON.stringify(image.landmarks)),
        cmPerPx: image.cmPerPx,
        custom: image.custom || {},
        ...(image.pelvisPinsOptOut ? { pelvisPinsOptOut: true } : {}),
        ...(image.captureSource ? { captureSource: image.captureSource } : {}),
        ...pickManualFloorCorrection(image),
      };
    }
  }
  return out;
}
